(() => {
  const MOBILE = '(max-width: 767px)';
  const AUTO_ROTATE_MS = 5000;
  const AUTO_ROTATE_RESUME_MS = 10000;

  class IconBannerCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-icon-banner-track]');
      this.slides = this.track
        ? [...this.track.querySelectorAll('[data-icon-banner-slide]')]
        : [];
      this.dots = [...this.querySelectorAll('[data-icon-banner-dot]')];
      this.mq = window.matchMedia(MOBILE);
      this.autoRotate =
        this.dataset.autoRotateMobile !== 'false' &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this._activeIndex = 0;

      if (!this.track || this.slides.length <= 1) return;

      this._onScroll = () => {
        if (!this.mq.matches) return;
        if (this._raf) cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(() => {
          this._raf = null;
          this._syncDots();
        });
      };

      this._onMqChange = () => {
        if (this.mq.matches) {
          this._maybeStartAutoRotate();
        } else {
          this._stopAutoRotate();
          this._resetToStart();
        }
      };

      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      this.mq.addEventListener('change', this._onMqChange);

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const i = Number(dot.dataset.iconBannerDot);
          const slide = this.slides[i];
          if (!slide || !this.mq.matches) return;
          this._pauseAutoRotate();
          this.track.scrollTo({
            left: slide.offsetLeft - (this.track.clientWidth - slide.offsetWidth) / 2,
            behavior: 'smooth',
          });
        });
      });

      if (this.autoRotate) {
        this.track.addEventListener('touchstart', () => this._pauseAutoRotate(), {
          passive: true,
        });
        this.track.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || this.mq.matches) {
            this._pauseAutoRotate();
          }
        });
      }

      this._resetToStart();
      requestAnimationFrame(() => {
        this._resetToStart();
        this._maybeStartAutoRotate();
      });
    }

    _resetToStart() {
      this.track.scrollLeft = 0;
      this._setActive(0);
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this._onScroll);
      this.mq?.removeEventListener('change', this._onMqChange);
      if (this._raf) cancelAnimationFrame(this._raf);
      this._stopAutoRotate();
    }

    _getActiveIndex() {
      const center = this.track.scrollLeft + this.track.clientWidth / 2;
      let active = 0;
      let nearest = Infinity;

      this.slides.forEach((slide, i) => {
        const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
        const dist = Math.abs(center - slideCenter);
        if (dist < nearest) {
          nearest = dist;
          active = i;
        }
      });

      return active;
    }

    _syncDots() {
      if (!this.mq.matches) return;
      this._activeIndex = this._getActiveIndex();
      this._setActive(this._activeIndex);
    }

    _setActive(index) {
      this._activeIndex = index;
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    _scrollToSlide(index, behavior = 'smooth') {
      const slide = this.slides[index];
      if (!slide) return;

      this.track.scrollTo({
        left: slide.offsetLeft,
        behavior,
      });
      this._setActive(index);
    }

    _advanceSlide() {
      if (!this.mq.matches) return;

      const next = (this._activeIndex + 1) % this.slides.length;
      this._scrollToSlide(next);
    }

    _maybeStartAutoRotate() {
      if (!this.autoRotate || !this.mq.matches || this.slides.length <= 1) return;
      this._startAutoRotate();
    }

    _startAutoRotate() {
      this._stopAutoRotate();
      this._autoRotateTimer = window.setInterval(
        () => this._advanceSlide(),
        AUTO_ROTATE_MS
      );
    }

    _stopAutoRotate() {
      if (this._autoRotateTimer) {
        clearInterval(this._autoRotateTimer);
        this._autoRotateTimer = null;
      }
      if (this._resumeTimer) {
        clearTimeout(this._resumeTimer);
        this._resumeTimer = null;
      }
    }

    _pauseAutoRotate() {
      if (!this.autoRotate) return;

      this._stopAutoRotate();
      this._resumeTimer = window.setTimeout(() => {
        this._resumeTimer = null;
        this._maybeStartAutoRotate();
      }, AUTO_ROTATE_RESUME_MS);
    }
  }

  if (!customElements.get('icon-banner-carousel')) {
    customElements.define('icon-banner-carousel', IconBannerCarousel);
  }
})();
